import React, {
  memo,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { COUNTRY_CODE } from '@constants';
import {
  getFormatedPhoneNumber,
  getObjectValue,
} from '@utility/helperFunction';
import EventEmitter from '@utility/eventSubscribers';
import { getStorePickDeliveryTimes } from '@reducers/checks';
import { setNotification } from '@reducers/notification';
import ModalHeader from '@components/common/ModalHeader/index';
import { LABEL } from '@veriables';
import store from '@store';

import { useSubmitTogo, useValidateTogo } from '@containers/StoreChecksV2/header/headerHandler';
import {
  formatTimeLabel,
  NameField,
  PhoneField,
  PickupTimeField,
  KeyboardSection,
  ActionButtons,
} from './createTogoFields';

const TogoCreateCheckModalV2 = ({
  isTogo,
  isLandScape,
  serverAppTheme,
  orderingPortraitOrientation,
  onCloseModal,
}) => {
  const isBrowser = useRef(!window.ReactNativeWebView).current;

  const callCenter = useMemo(
    () => JSON.parse(window.sessionStorage.getItem('callCenter')),
    []
  );
  const defaultCountryCode = useMemo(() => {
    const auth = store.getState().oauth.authInfo;
    return String(
      getObjectValue(() => auth?.country && COUNTRY_CODE[auth.country]?.dialCode, 1)
    );
  }, []);
  const defaultMaxLength = useMemo(() => {
    const auth = store.getState().oauth.authInfo;
    return getObjectValue(() => COUNTRY_CODE?.[auth?.country]?.maxLength, 15);
  }, []);

  const initialPhone = getFormatedPhoneNumber(callCenter?.ANI) ?? '';

  const [checkName, setCheckName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(initialPhone);
  const [pickupTime, setPickupTime] = useState('');
  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  const [phoneNumberMaxLength] = useState(defaultMaxLength);
  const [options, setOptions] = useState([]);

  const [openKeyboard, setOpenKeyboard] = useState(false);
  const [inputName, setInputName] = useState('');
  const keyboardRef = useRef(null);
  const typedRef = useRef({
    checkName: '',
    phoneNumber: initialPhone.replace(/\D/g, ''),
  });

  const phoneNumberRef = useRef(phoneNumber);
  phoneNumberRef.current = phoneNumber;
  const phoneNumberMaxLengthRef = useRef(phoneNumberMaxLength);
  phoneNumberMaxLengthRef.current = phoneNumberMaxLength;
  const countryCodeRef = useRef(countryCode);
  countryCodeRef.current = countryCode;
  const latestFormRef = useRef({});
  latestFormRef.current = { checkName, phoneNumber, pickupTime, countryCode };

  const submitTogo = useSubmitTogo();
  const validateTogo = useValidateTogo();

  useLayoutEffect(() => {
    if (window.ReactNativeWebView) {
      EventEmitter.changeOrientation({
        orientation: orderingPortraitOrientation ? 'portrait' : 'landscape',
      });
    }
  }, [orderingPortraitOrientation]);

  useEffect(() => {
    const { oauth } = store.getState();
    const { deviceInformation } = oauth;
    const authData = JSON.parse(window.sessionStorage.getItem('auth'));

    store
      .dispatch(
        getStorePickDeliveryTimes({
          storeId: deviceInformation.storeId,
          deviceId: deviceInformation.deviceId,
          employeeId: authData.callCenterEmployeeId,
        })
      )
      .then(val => {
        const formatted = val.pickupTimes.map(formatTimeLabel);
        if (formatted.length > 0) formatted[0] += ' (ASAP)';
        setOptions(formatted);
        setCountryCode('1');
        setPickupTime(formatted[0] ?? '');
      })
      .catch(e => {
        setCountryCode('1');
        store.dispatch(setNotification({ type: 'danger', message: e.message }));
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!openKeyboard) return;
    const handleOutside = e => {
      const isInput =
        e.target.id === 'checkName' || e.target.id === 'phoneNumber';
      if (
        !isInput &&
        keyboardRef.current &&
        !keyboardRef.current.contains(e.target)
      ) {
        setOpenKeyboard(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [openKeyboard]);

  const onChangeName = useCallback(e => {
    const val = e.target.value;
    typedRef.current.checkName = val;
    setCheckName(val);
  }, []);

  const onChangePhone = useCallback(e => {
    const rawVal = e.target.value;
    const digits = rawVal.replace(/\D/g, '');
    const maxDigits = countryCodeRef.current === '1' ? 10 : 15;
    const truncated = digits.substring(0, maxDigits);
    typedRef.current.phoneNumber = truncated;
    const formatted =
      Number(countryCodeRef.current) === 1
        ? getFormatedPhoneNumber(truncated)
        : truncated;
    if (e.target.value !== formatted) {
      e.target.value = formatted;
    }
    setPhoneNumber(formatted);
  }, []);

  const onClearName = useCallback(() => {
    setCheckName('');
    typedRef.current.checkName = '';
  }, []);

  const onClearPhone = useCallback(() => {
    setPhoneNumber('');
    typedRef.current.phoneNumber = '';
  }, []);

  const onNameFocus = useCallback(() => {
    setInputName('checkName');
    if (!isBrowser) {
      setOpenKeyboard(true);
      document.querySelector('#checkName')?.blur();
    }
  }, [isBrowser]);

  const onPhoneFocus = useCallback(() => {
    setInputName('phoneNumber');
    if (isBrowser) {
      setOpenKeyboard(false);
    } else {
      setOpenKeyboard(true);
      document.querySelector('#phoneNumber')?.blur();
    }
  }, [isBrowser]);

  const onCountryCodeChange = useCallback(id => setCountryCode(id), []);
  const onPickupTimeChange = useCallback(val => setPickupTime(val), []);
  const closeKeyboard = useCallback(() => setOpenKeyboard(false), []);

  const onContinue = useCallback(() => {
    const {
      checkName: name,
      phoneNumber: phone,
      countryCode: cc,
      pickupTime: pickup,
    } = latestFormRef.current;

    if (!validateTogo({ name, phone, countryCode: cc, pickup })) return;
    submitTogo({ name, phone, countryCode: cc, pickup });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyboardInput = useCallback((key, field) => {
    const isBackspace = key === '{bksp}';
    if (field === 'phoneNumber' && !isBackspace && /\D/.test(key)) return;

    const current = typedRef.current[field] || '';
    const maxLen = phoneNumberRef.current?.includes('-')
      ? 10
      : phoneNumberMaxLengthRef.current;

    if (field === 'phoneNumber' && !isBackspace && current.length >= maxLen)
      return;

    const next = isBackspace ? current.slice(0, -1) : current + key;
    typedRef.current[field] = next;

    if (field === 'phoneNumber') {
      setPhoneNumber(
        Number(countryCodeRef.current) === 1
          ? getFormatedPhoneNumber(next)
          : next
      );
    } else {
      setCheckName(next);
    }
  }, []);

  const maxLengthForKeyboard = useMemo(
    () => (phoneNumber?.includes('-') ? 10 : phoneNumberMaxLength),
    [phoneNumber, phoneNumberMaxLength]
  );

  const typedForKeyboard = typedRef.current[inputName] ?? '';

  return (
    <div style={{ background: '#f5f7fb', height: '100vh' }}>
      <div className="col-12 h-100 px-0 py-0 justify-content-center align-items-center position-relative text-center od-text-primary text-primary">
        <div style={{ color: serverAppTheme?.primary_text }}>
          <ModalHeader isTab={false} tileHeader={LABEL().TOGO} />
        </div>

        <div className="mt-100">
          <NameField
            value={checkName}
            onChange={onChangeName}
            onClear={onClearName}
            onFocus={onNameFocus}
            theme={serverAppTheme}
            isPortrait={orderingPortraitOrientation}
          />

          <PhoneField
            value={phoneNumber}
            countryCode={countryCode}
            onChange={onChangePhone}
            onClear={onClearPhone}
            onFocus={onPhoneFocus}
            onCountryCodeChange={onCountryCodeChange}
            theme={serverAppTheme}
            isPortrait={orderingPortraitOrientation}
            isLandScape={isLandScape}
            isTogo={isTogo}
            isBrowser={isBrowser}
          />

          <PickupTimeField
            value={pickupTime}
            options={options}
            onChange={onPickupTimeChange}
            theme={serverAppTheme}
            isPortrait={orderingPortraitOrientation}
          />
        </div>

        {openKeyboard && (
          <KeyboardSection
            keyboardRef={keyboardRef}
            inputName={inputName}
            typed={typedForKeyboard}
            onKeyboardInput={onKeyboardInput}
            onClose={closeKeyboard}
            isPortrait={orderingPortraitOrientation}
            isLandScape={isLandScape}
            maxLength={maxLengthForKeyboard}
          />
        )}

        <ActionButtons
          onCloseModal={onCloseModal}
          onContinue={onContinue}
          theme={serverAppTheme}
          isPortrait={orderingPortraitOrientation}
        />
      </div>
    </div>
  );
};

export default memo(TogoCreateCheckModalV2);
